import { useCallback, useEffect, useState } from 'react';
import { createRoom, fetchMeeting, fetchMeetings, fetchRooms } from './api';
import type { Me, Meeting, RoomState } from './api';
import { InviteButton } from './InviteButton';
import { Markdown } from './Markdown';
import { MeetingList, TranscriptProgress } from './Meetings';
import type { Route } from './routes';

/**
 * The front door.
 *
 * It exists because in the system this was extracted from, the front door WAS a
 * meeting: opening the site put you on camera in front of whoever was already
 * there, with no way to find out first. That is a strange thing for a website to
 * do, and it made the summaries — the part of this system people actually read
 * between meetings — reachable only by going through a call.
 *
 * So the landing page answers the two questions someone opening this site
 * actually has: is the meeting happening, and what did I miss.
 */

/** How often to re-ask who is in the room. */
const REFRESH_MS = 10_000;

/** How many meetings to list under the latest one. */
const RECENT = 5;

function names(m: Meeting): string {
  // Defensive against `null` for the same reason Meetings.tsx is: Go marshals a
  // nil slice that way, and a meeting with no participants once blanked the page.
  return (m.participants ?? [])
    .map((p) => p.name)
    .filter(Boolean)
    .join(', ');
}

function when(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function duration(m: Meeting): string {
  if (!m.endedAt) return 'in progress';
  const secs = Math.round((Date.parse(m.endedAt) - Date.parse(m.startedAt)) / 1000);
  const h = Math.floor(secs / 3600);
  const min = Math.round((secs % 3600) / 60);
  return h > 0 ? `${h}h ${min}m` : `${min}m`;
}

/** "for 12 min", counted from when the room was created. */
function runningFor(startedAt?: string): string {
  if (!startedAt) return '';
  const mins = Math.floor((Date.now() - Date.parse(startedAt)) / 60000);
  if (!Number.isFinite(mins) || mins < 1) return 'just started';
  if (mins < 60) return `going ${mins} min`;
  const h = Math.floor(mins / 60);
  return `going ${h}h ${mins % 60}m`;
}

/**
 * Who is in there, in words.
 *
 * Names, not a number, up to the point where a list stops being readable — the
 * useful thing to know before joining is WHO is waiting, not how many.
 */
function whoIsHere(r: RoomState): string {
  const p = r.people;
  if (!r.live) return 'Nobody here yet';
  if (p.length === 0) return `${r.count} ${r.count === 1 ? 'person' : 'people'} here`;
  if (p.length === 1) return `${p[0]} is here`;
  if (p.length === 2) return `${p[0]} and ${p[1]} are here`;
  return `${p.slice(0, 2).join(', ')} and ${p.length - 2} more are here`;
}

function RoomCard({
  room,
  canInvite,
  onJoin,
}: {
  room: RoomState;
  canInvite: boolean;
  onJoin: () => void;
}) {
  return (
    <section className={`mx-room-card${room.live ? ' live' : ''}`}>
      <div className="mx-room-head">
        <h2>{room.title || room.name}</h2>
        {/* An ad-hoc meeting's NAME is not its title, and the name is what you
            paste to somebody. Standing rooms are named after themselves, so
            printing it twice would be noise. */}
        {room.adhoc && room.title !== room.name && (
          <span className="mx-room-name">{room.name}</span>
        )}
        {room.live && <span className="mx-live" aria-label="in progress">live</span>}
        {/* Only alongside `live`. The SFU keeps reporting a room as being
            recorded for the few seconds between the last person leaving and the
            recorder timing out — and "recording" next to "nobody here yet" reads
            as something taping an empty room. */}
        {room.live && room.recording && <span className="mx-pill">recording</span>}
      </div>

      <p className="mx-room-who">
        {whoIsHere(room)}
        {room.live && room.startedAt && <span className="mx-muted"> · {runningFor(room.startedAt)}</span>}
      </p>

      <div className="mx-room-actions">
        <button className="mx-button mx-button-inline" onClick={onJoin}>
          {room.live ? 'Join the meeting' : 'Start the meeting'}
        </button>
        <InviteButton room={room.name} canInvite={canInvite} />
      </div>

      <p className="mx-muted">
        Meetings are recorded — one audio and video track per person — then transcribed and
        summarised. Everyone who was there gets the summary by email.
      </p>
    </section>
  );
}

/**
 * Start a meeting that is not one of the standing rooms.
 *
 * WHY THIS EXISTS AT ALL: rooms used to be a list in the operator's NixOS
 * configuration, so holding a call about something that was not a weekly
 * standing meeting meant a deploy. The name comes back from the server with a
 * random suffix — two people naming a meeting "standup" ten minutes apart must
 * not land in each other's call — and the title is what everyone sees, in the
 * dashboard, in the summary email and in the manifest afterwards.
 */
function StartMeeting({ onStarted }: { onStarted: (room: string) => void }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = () => {
    setBusy(true);
    setError(null);
    createRoom(title.trim())
      .then((r) => onStarted(r.name))
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : String(e));
        setBusy(false);
      });
  };

  if (!open) {
    return (
      <section className="mx-room-card mx-start">
        <div className="mx-room-head">
          <h2>Start a meeting</h2>
        </div>
        <p className="mx-muted">
          For anything that is not one of the rooms above. It gets its own link, it records like
          any other room, and it disappears when everyone leaves.
        </p>
        <div className="mx-room-actions">
          <button className="mx-button mx-button-inline" onClick={() => setOpen(true)}>
            Start a meeting
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="mx-room-card mx-start">
      <div className="mx-room-head">
        <h2>Start a meeting</h2>
      </div>
      <form
        className="mx-start-form"
        onSubmit={(e) => {
          e.preventDefault();
          start();
        }}
      >
        <label>
          What is it about?
          <input
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Product sync"
            maxLength={120}
          />
        </label>
        <div className="mx-room-actions">
          <button className="mx-button mx-button-inline" type="submit" disabled={busy}>
            {busy ? 'Starting…' : 'Start'}
          </button>
          <button className="mx-invite-btn" type="button" onClick={() => setOpen(false)}>
            Cancel
          </button>
        </div>
      </form>
      {error && <p className="mx-error">{error}</p>}
      <p className="mx-muted">
        The title travels with the recording — it is what the archive, the summary email and the
        manifest all call this meeting afterwards.
      </p>
    </section>
  );
}

/**
 * The last meeting, summary and all.
 *
 * The summary is rendered here rather than linked to, because "what did I miss"
 * is the question this page exists to answer and one more click to answer it is
 * one click too many. The transcript stays behind the link — it is the primary
 * source you consult, not the thing you read.
 */
function LatestMeeting({ meeting, onOpen }: { meeting: Meeting; onOpen: () => void }) {
  return (
    <section className="mx-dash-latest">
      <div className="mx-room-head">
        {/* "Latest summary", not "Last meeting": it is the most recent meeting
            anyone SPOKE in, which is frequently not the most recent meeting. */}
        <h2>Latest summary</h2>
        <button className="mx-back" onClick={onOpen}>
          Open it →
        </button>
      </div>
      <p className="mx-muted">
        {when(meeting.startedAt)} · {duration(meeting)}
        {names(meeting) && ` · ${names(meeting)}`}
      </p>
      {/* The same renderer the archive uses. This panel is fed by fetchMeeting,
          so it has the transcript body and can tell "still arriving" from
          "never arrived" — which the row in a listing cannot. */}
      {meeting.summary ? <Markdown text={meeting.summary} /> : <TranscriptProgress m={meeting} />}
    </section>
  );
}

export function Dashboard({ me, navigate }: { me: Me; navigate: (r: Route) => void }) {
  const [rooms, setRooms] = useState<RoomState[] | null>(null);
  const [meetings, setMeetings] = useState<Meeting[] | null>(null);
  const [latest, setLatest] = useState<Meeting | null>(null);
  // The WRITTEN record — summaries and transcripts — is for everyone on staff;
  // that is what summarising meetings is for. A guest was invited to one meeting
  // and has no back catalogue, so everything below the rooms is rendered only
  // where it can actually be served rather than offering a button that 403s.
  // (The RAW MEDIA is still operator-only; that link lives in the nav now.)
  const staff = !me.guest;

  const loadRooms = useCallback(() => {
    // A failure here is left to the Status page rather than shown: the SFU being
    // briefly unreachable must not replace the Join button with an error.
    fetchRooms()
      .then(setRooms)
      .catch(() => setRooms((prev) => prev ?? []));
  }, []);

  useEffect(() => {
    loadRooms();
    const t = setInterval(() => {
      // Not while nobody is looking. An abandoned tab polling the SFU forever is
      // exactly the sort of idle cost this deployment does not need.
      if (document.visibilityState === 'visible') loadRooms();
    }, REFRESH_MS);
    const onVisible = () => document.visibilityState === 'visible' && loadRooms();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [loadRooms]);

  useEffect(() => {
    if (!staff) return; // the list is the back catalogue; a guest was invited to one meeting
    let cancelled = false;
    fetchMeetings()
      .then((list) => {
        if (cancelled) return;
        setMeetings(list);
        // The newest meeting SOMEBODY SPOKE IN — not merely the newest one with a
        // summary file. Every silent test join gets a one-line stub summary, so
        // picking on hasSummary meant this panel permanently read "no speech was
        // recorded in this meeting" while the one real summary sat nine rows down
        // the list, which is exactly how it looked like there were no summaries
        // at all.
        const first = list.find((m) => m.transcriptLines > 0);
        if (!first) return;
        fetchMeeting(first.room, first.session)
          .then((full) => !cancelled && setLatest(full))
          .catch(() => {});
      })
      .catch(() => !cancelled && setMeetings([]));
    return () => {
      cancelled = true;
    };
  }, [staff]);

  const earlier = (meetings ?? []).filter((m) => m.session !== latest?.session).slice(0, RECENT);

  // The frame supplies the bar, the nav and the account menu (AppShell). This
  // page is only its own content now — it used to carry a top bar with the
  // brand, who you are and Sign out, and a footer nav that was the app's real
  // site map.
  return (
    <>
      {rooms === null ? (
        <p className="mx-muted">Checking who is around…</p>
      ) : rooms.length === 0 ? (
        <p className="mx-muted">No rooms are configured.</p>
      ) : (
        rooms.map((r) => (
          <RoomCard
            key={r.name}
            room={r}
            canInvite={staff}
            onJoin={() => navigate({ name: 'call', room: r.name })}
          />
        ))
      )}

      {staff && <StartMeeting onStarted={(room) => navigate({ name: 'call', room })} />}

      {staff && (
        <>
          {latest && (
            <LatestMeeting
              meeting={latest}
              onOpen={() => navigate({ name: 'meeting', room: latest.room, session: latest.session })}
            />
          )}

          {earlier.length > 0 && (
            <section className="mx-dash-section">
              <div className="mx-room-head">
                <h2>{latest ? 'Other meetings' : 'Meetings'}</h2>
                <button className="mx-back" onClick={() => navigate({ name: 'meetings' })}>
                  All {meetings?.length ?? 0} →
                </button>
              </div>
              {/* The same renderer the archive uses. Two copies of a row that
                  now has a title in it is two places to forget to show it. */}
              <MeetingList list={earlier} navigate={navigate} />
            </section>
          )}

          {meetings !== null && meetings.length === 0 && (
            <p className="mx-muted">
              No meetings have been recorded yet. The first one you hold will appear here with
              its summary and transcript.
            </p>
          )}
        </>
      )}
    </>
  );
}
